import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Client } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { deathEventQueryKey, useDeathEvent } from "@/lib/use-death-event";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { formatTimeLeft, zoneForJurisdiction } from "@shared/jurisdiction-time";

/**
 * DEATH-EMAILS-1 (s229, D-229.48 / D-229.65) — the planner's "Stop the release" banner.
 *
 * Mounted ONCE above every planner surface in App.tsx — the interview intro, the interview shell /
 * plan home (PlanApp) and the old dashboard (AppShell) — so email 18's "Sign in and stop it" lands
 * on it wherever the planner's sign-in takes them. Renders nothing unless the signed-in planner's
 * own plan has a report in its safety wait.
 *
 * Wording is D-229.48's: the reporting executor's first name, the real date, the real time left
 * from the planner's own wait. The board's "her" is written "them": the product does not know the
 * reporter's pronouns (flagged for Reade in the s229 report).
 *
 * The button calls the existing DELETE /death-event under the single-flight lock (D-169.10).
 */
export function StopReleaseBanner({ userId, isPlanner }: { userId?: string; isPlanner: boolean }) {
  const { toast } = useToast();
  const flight = useSingleFlight();

  // Same key and fetch as ClientDashboard / useInterviewGate, so TanStack shares the one request.
  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/clients", userId],
    queryFn: async () => (await apiRequest("GET", "/api/clients")).json(),
    enabled: isPlanner && !!userId,
  });
  const client = clients?.[0];
  const { data: event } = useDeathEvent(client?.id, { enabled: isPlanner && !!client?.id });

  // Re-render once a minute so the time left stays true while the page is open.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const stop = useMutation({
    mutationFn: async () => (await apiRequest("DELETE", `/api/clients/${client!.id}/death-event`)).json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deathEventQueryKey(client?.id) });
      toast({ title: "The release is stopped", description: "Your plan stays closed." });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't stop the release", description: err.message, variant: "destructive" });
    },
  });

  if (!isPlanner || !client || !event || event.status !== "pending_grace_period" || !event.gracePeriodEndsAt) return null;

  const zone = zoneForJurisdiction((client as any).jurisdictionCountry, (client as any).jurisdictionProvince);
  const reportedOn = event.createdAt
    ? new Date(event.createdAt).toLocaleDateString("en-CA", { timeZone: zone.timeZone, month: "long", day: "numeric", year: "numeric" })
    : "";
  const timeLeft = formatTimeLeft(new Date(event.gracePeriodEndsAt).getTime() - now);
  const who = (event.reporterFirstName as string | null | undefined) || "Your executor";

  return (
    <div role="alert" data-testid="stop-release-banner"
      className="w-full border-b border-red-300 bg-red-50 px-4 py-3 text-red-950">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <p className="text-sm leading-relaxed">
          {who} reported your death on {reportedOn}. If this is a mistake, stop it now — your plan opens to them in {timeLeft}.
        </p>
        <Button
          data-testid="stop-release-button"
          variant="destructive"
          disabled={flight.inFlight}
          onClick={() => flight.runQuiet(() => stop.mutateAsync())}
        >
          Stop the release
        </Button>
      </div>
    </div>
  );
}
